import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  AlertTriangle, Banknote, CheckCircle2, XCircle, Clock,
  Search, X, Eye, UserX, Ban, Filter, Loader2, ExternalLink, CreditCard, Send,
  User, FileText, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const statusStyles = {
  pending: 'bg-amber-100 text-amber-800',
  payment_pending: 'bg-indigo-100 text-indigo-800',
  paid: 'bg-emerald-100 text-emerald-800',
  waived: 'bg-slate-100 text-slate-600',
};

const typeLabels = {
  late_cancellation: 'Late Cancel',
  no_show: 'No-Show',
  till_discrepancy: 'Till Issue',
  other: 'Other',
};

const typeIcons = {
  late_cancellation: Ban,
  no_show: UserX,
  till_discrepancy: Banknote,
  other: AlertTriangle,
};

export default function PenaltiesDashboard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [penalties, setPenalties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailPenalty, setDetailPenalty] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [payRefDialog, setPayRefDialog] = useState(null);
  const [paymentRef, setPaymentRef] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('penalty_type', typeFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [penaltiesRes, summaryRes] = await Promise.all([
        api.get(`/penalties/all${qs}`),
        api.get('/penalties/summary')
      ]);
      setPenalties(penaltiesRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error('Failed to load penalties');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleWaive = async (penaltyId) => {
    setActionLoading(penaltyId);
    try {
      await api.patch(`/penalties/${penaltyId}/waive`);
      toast.success('Penalty waived');
      fetchData();
      if (detailPenalty?.id === penaltyId) fetchAuditLog(penaltyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to waive');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (penaltyId) => {
    setActionLoading(penaltyId);
    try {
      const params = paymentRef ? `?payment_reference=${encodeURIComponent(paymentRef)}` : '';
      await api.patch(`/penalties/${penaltyId}/mark-paid${params}`);
      toast.success('Penalty marked as paid');
      setPayRefDialog(null);
      setPaymentRef('');
      fetchData();
      if (detailPenalty?.id === penaltyId) fetchAuditLog(penaltyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setActionLoading(null);
    }
  };

  // Trigger Stripe charge — creates a checkout session and emails the
  // worker a secure payment link.
  const handleChargeViaStripe = async (penalty) => {
    setActionLoading(penalty.id);
    try {
      const res = await api.post(`/penalties/${penalty.id}/charge`, {
        originUrl: window.location.origin,
      });
      toast.success(`Payment link sent to ${res.data?.workerEmail || 'worker'}`);
      fetchData();
      if (detailPenalty?.id === penalty.id) fetchAuditLog(penalty.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Stripe charge');
    } finally {
      setActionLoading(null);
    }
  };

  const fetchAuditLog = async (penaltyId) => {
    try {
      const res = await api.get(`/penalties/${penaltyId}/audit-log`);
      setAuditLogs(res.data);
    } catch {
      setAuditLogs([]);
    }
  };

  const openDetail = (penalty) => {
    setDetailPenalty(penalty);
    fetchAuditLog(penalty.id);
  };

  const filtered = penalties.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.workerName?.toLowerCase().includes(q) ||
           p.shiftBranch?.toLowerCase().includes(q) ||
           p.reason?.toLowerCase().includes(q);
  });

  const formatDate = (str) => {
    if (!str) return '-';
    const d = new Date(str);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (str) => {
    if (!str) return '';
    const d = new Date(str);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-12 w-full" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="penalties-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">Penalties</h1>
        <p className="text-slate-500 mt-1">Track and manage worker penalties</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard icon={AlertTriangle} label="Total Penalties" value={summary.totalPenalties} color="text-slate-700 bg-slate-50" />
          <SummaryCard icon={Clock} label="Pending" value={`£${summary.pendingAmount.toFixed(2)}`} color="text-amber-700 bg-amber-50" />
          <SummaryCard icon={CheckCircle2} label="Paid" value={`£${summary.paidAmount.toFixed(2)}`} color="text-emerald-700 bg-emerald-50" />
          <SummaryCard icon={XCircle} label="Waived" value={`£${summary.waivedAmount.toFixed(2)}`} color="text-slate-500 bg-slate-50" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search worker, branch, or reason..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200"
            data-testid="penalty-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 h-11 rounded-xl" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="waived">Waived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-40 h-11 rounded-xl" data-testid="type-filter">
            <SelectValue placeholder="Reason" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reasons</SelectItem>
            <SelectItem value="late_cancellation">Late Cancel</SelectItem>
            <SelectItem value="no_show">No-Show</SelectItem>
            <SelectItem value="till_discrepancy">Till Issue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-slate-500">{filtered.length} penalt{filtered.length !== 1 ? 'ies' : 'y'}</p>

      {/* Penalties List */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Penalties Found</h3>
            <p className="text-slate-500">{statusFilter !== 'all' || typeFilter !== 'all' ? 'Try changing the filters.' : 'No penalties recorded yet.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => {
            const TypeIcon = typeIcons[p.type] || AlertTriangle;
            return (
              <Card key={p.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`penalty-row-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Type Icon */}
                    <div className="h-11 w-11 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                      <TypeIcon className="h-5 w-5 text-red-600" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-5 gap-y-1 gap-x-4 items-center">
                      <div className="sm:col-span-2">
                        <p className="font-semibold text-slate-900 truncate">{p.workerName}</p>
                        <p className="text-xs text-slate-500 truncate">{p.shiftBranch} &middot; {p.shiftDate}</p>
                      </div>
                      <div>
                        <Badge className="bg-red-50 text-red-700 text-xs">{typeLabels[p.type] || p.type}</Badge>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-slate-900">&pound;{p.amount?.toFixed(2)}</span>
                      </div>
                      <div className="text-right">
                        <Badge className={statusStyles[p.penaltyStatus] || 'bg-slate-100'}>{p.penaltyStatus}</Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost" size="sm" className="h-9 w-9 p-0 text-slate-400 hover:text-slate-700"
                        onClick={() => openDetail(p)}
                        data-testid={`view-penalty-${p.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {(p.penaltyStatus === 'pending' || p.penaltyStatus === 'payment_pending') && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            className="h-9 w-9 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                            onClick={() => handleChargeViaStripe(p)}
                            disabled={actionLoading === p.id}
                            title={p.penaltyStatus === 'payment_pending' ? 'Resend payment link' : 'Send Stripe payment link to worker'}
                            data-testid={`charge-stripe-${p.id}`}
                          >
                            {actionLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (p.penaltyStatus === 'payment_pending' ? <Send className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />)}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="h-9 w-9 p-0 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setPayRefDialog(p)}
                            disabled={actionLoading === p.id}
                            title="Mark as paid manually"
                            data-testid={`mark-paid-${p.id}`}
                          >
                            {actionLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="h-9 w-9 p-0 text-slate-400 hover:text-slate-700 hover:bg-slate-50"
                            onClick={() => handleWaive(p.id)}
                            disabled={actionLoading === p.id}
                            title="Waive penalty"
                            data-testid={`waive-penalty-${p.id}`}
                          >
                            {actionLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 ml-15 pl-[60px]">{p.reason} &middot; {formatDate(p.createdAt)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Penalty Detail Dialog */}
      <Dialog open={!!detailPenalty} onOpenChange={(open) => !open && setDetailPenalty(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Penalty Details</DialogTitle></DialogHeader>
          {detailPenalty && (
            <div className="space-y-5 py-2">
              {/* Worker & Shift */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Worker</p>
                  <p className="font-semibold text-slate-900">{detailPenalty.workerName}</p>
                  <p className="text-xs text-slate-500">{detailPenalty.workerEmail}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Shift</p>
                  <p className="font-semibold text-slate-900">{detailPenalty.shiftBranch}</p>
                  <p className="text-xs text-slate-500">{detailPenalty.shiftDate}</p>
                </div>
              </div>

              {/* Amount & Status */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">&pound;{detailPenalty.amount?.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">Amount</p>
                </div>
                <div className="text-center">
                  <Badge className={`${statusStyles[detailPenalty.penaltyStatus]} text-sm`}>{detailPenalty.penaltyStatus}</Badge>
                  <p className="text-xs text-slate-500 mt-1">Status</p>
                </div>
                <div className="text-center">
                  <Badge className="bg-red-50 text-red-700 text-sm">{typeLabels[detailPenalty.type] || detailPenalty.type}</Badge>
                  <p className="text-xs text-slate-500 mt-1">Type</p>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Reason</p>
                <p className="text-sm text-slate-700">{detailPenalty.reason}</p>
              </div>

              {/* Payment Reference */}
              {detailPenalty.paymentReference && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Payment Reference</p>
                  <p className="text-sm text-slate-700 font-mono">{detailPenalty.paymentReference}</p>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-slate-700">{formatDate(detailPenalty.createdAt)} {formatTime(detailPenalty.createdAt)}</p>
                </div>
                {detailPenalty.waivedAt && (
                  <div>
                    <p className="text-xs text-slate-500">Waived</p>
                    <p className="text-slate-700">{formatDate(detailPenalty.waivedAt)} {formatTime(detailPenalty.waivedAt)}</p>
                  </div>
                )}
                {detailPenalty.paidAt && (
                  <div>
                    <p className="text-xs text-slate-500">Paid</p>
                    <p className="text-slate-700">{formatDate(detailPenalty.paidAt)} {formatTime(detailPenalty.paidAt)}</p>
                  </div>
                )}
              </div>

              {/* Audit Trail */}
              {auditLogs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Audit Trail</p>
                  <div className="space-y-2">
                    {auditLogs.map(log => (
                      <div key={log.id} className="flex items-start gap-3 text-sm p-3 bg-slate-50 rounded-lg">
                        <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-slate-700">
                            <span className="font-medium">{log.managerName}</span>
                            {' '}changed status from <Badge className="text-xs bg-slate-200">{log.previousStatus}</Badge>
                            {' '}to <Badge className={`text-xs ${statusStyles[log.newStatus] || 'bg-slate-200'}`}>{log.newStatus}</Badge>
                          </p>
                          {log.paymentReference && <p className="text-xs text-slate-500 mt-1">Ref: {log.paymentReference}</p>}
                          <p className="text-xs text-slate-400 mt-1">{formatDate(log.createdAt)} {formatTime(log.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                <Button
                  variant="outline" size="sm" className="rounded-full text-sm"
                  onClick={() => { setDetailPenalty(null); navigate(`/manager/staff`); }}
                  data-testid="goto-worker-profile"
                >
                  <User className="h-3.5 w-3.5 mr-1.5" /> Worker Profile
                </Button>
                <Button
                  variant="outline" size="sm" className="rounded-full text-sm"
                  onClick={() => { setDetailPenalty(null); navigate(`/manager/my-shifts`); }}
                  data-testid="goto-shift"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View Shift
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={!!payRefDialog} onOpenChange={(open) => { if (!open) { setPayRefDialog(null); setPaymentRef(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Mark Penalty as Paid</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-slate-600">
              Mark the &pound;{payRefDialog?.amount?.toFixed(2)} penalty for <strong>{payRefDialog?.workerName}</strong> as paid.
            </p>
            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="e.g., TXN-12345 or Stripe PI reference"
                value={paymentRef}
                onChange={e => setPaymentRef(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="payment-ref-input"
              />
              <p className="text-xs text-slate-500">Ready for future Stripe integration</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPayRefDialog(null); setPaymentRef(''); }} className="rounded-full">Cancel</Button>
            <Button
              onClick={() => handleMarkPaid(payRefDialog?.id)}
              disabled={actionLoading === payRefDialog?.id}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-mark-paid"
            >
              {actionLoading === payRefDialog?.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="border-0 shadow-sm" data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
